import React from "react";
import { withCommas } from "../helpers";
import { LEVELS, Scenario, Municipality, Blockgroup, Parcel } from "../types";

type LayerKey = keyof Blockgroup | keyof Parcel | keyof Municipality;

export type Indicator = {
  label: string;
  short: string;
  explanation?: string;
  format: (value: any, kind?: any) => string | React.ReactNode;
  sliderOptions?: any;
  field: LayerKey;
  parcelField?: LayerKey;
  range?: [number, number];
  expression: mapboxgl.FillPaint;
};

type IndicatorMap = { [key: string]: Indicator };

type MapLayer = mapboxgl.AnyLayer & {
  before?: string;
};

type LevelLayersIndex = {
  [key in LEVELS]: string[];
};

export const gridcodes = {
  1: "canopy",
  2: "grass",
  3: "water",
  4: "soil",
  5: "buildings",
  6: "road",
  7: "other",
};

export const HEALTH_EQUITY_ZONES = [
  "AWCF",
  "WOON",
  "NEWPT",
  "EPROV",
  "BRIST",
  "WASH",
  "WWAR",
  "WELM",
  "CRAN",
  "CPROV",
];

export const DEFAULT_TREE_TYPE = "BDM";

const LAYER_BEFORE = "waterway-label";
export const BLOCKGROUP_DATA_LAYER = "layer-block-fills-data";

export const GREEN = "#6CC296";
export const ORANGE = "#f99d3e";

export const BLOCKGROUP_ZOOM = 10;
export const PARCEL_ZOOM = 14;
export const CANOPY_ZOOM = 16;

const DEFAULT_COLOR = "#888";
const HIGHLIGHT_COLOR = "#000";

export const WATERSHEDS = [
  "Abbott Run",
  "Aquidneck Island-Frontal Atlantic Ocean",
  "Barrington River-Warren River",
  "Branch River",
  "Chepachet River",
  "Chipuxet River-Pawcatuck River",
  "Clear River",
  "Emerson Brook-Blackstone River",
  "Greenwich Bay",
  "Headwaters South Branch Pawtuxet River",
  "Hunt River",
  "Lower East Passage",
  "Lower Pawcatuck River",
  "Lower West Passage",
  "Mill River",
  "Moshassuck River",
  "Moswansicut Pond-Huntinghouse Brook",
  "Mount Hope Bay",
  "Ninigret Pond-Frontal Block Island Sound",
  "North Branch Pawtuxet River",
  "Palmer River",
  "Pawtuxet River",
  "Peters River-Blackstone River",
  "Pettaquamscutt River-Frontal Atlantic Ocean",
  "Pocassett River",
  "Point Judith Pond-Frontal Block Island Sound",
  "Sakonnet River",
  "Saugatucket River",
  "Seekonk River-Providence River",
  "South Branch Pawtuxet River",
  "Ten Mile River",
  "Tomaquag Brook-Pawcatuck River",
  "Upper East Passage",
  "Upper Narragansett Bay",
  "Upper West Passage",
  "Usquepaug River",
  "Usquepaug River-Pawcatuck River",
  "Westport River-Frontal Rhode Island Sound",
  "Woonasquatucket River",
];

export const MUNI_NAMES = [
  "BARRINGTON",
  "BRISTOL",
  "BURRILLVILLE",
  "CENTRAL FALLS",
  "CHARLESTOWN",
  "COVENTRY",
  "CRANSTON",
  "CUMBERLAND",
  "EAST GREENWICH",
  "EAST PROVIDENCE",
  "EXETER",
  "GLOCESTER",
  "HOPKINTON",
  "JAMESTOWN",
  "JOHNSTON",
  "LINCOLN",
  "MIDDLETOWN",
  "NARRAGANSETT",
  "NEWPORT",
  "NORTH KINGSTOWN",
  "NORTH PROVIDENCE",
  "NORTH SMITHFIELD",
  "PAWTUCKET",
  "PORTSMOUTH",
  "PROVIDENCE",
  "SCITUATE",
  "SMITHFIELD",
  "SOUTH KINGSTOWN",
  "TIVERTON",
  "WARREN",
  "WARWICK",
  "WEST GREENWICH",
  "WEST WARWICK",
  "WESTERLY",
  "WOONSOCKET",
];

const MUNICIPALITY_LINE_WIDTH_DEFAULT = 2;
const MUNICIPALITY_LINE_WIDTH_HIGHLIGHT = 3;

export const formatPercent = (value: any) =>
  typeof value == "number" ? `${(value * 100).toFixed()}%` : "";
const formatFixed = (value: any) =>
  typeof value === "number" ? value.toFixed() : "";
const formatIdentity = (value: any) => value;
const formatScore = (value: any) => (value * 100).toFixed();

export const INDICATORS: IndicatorMap = {
  TREE_EQUITY_SCORE: {
    label: "Tree Equity Score",
    short: "Tree Equity Score",
    explanation:
      "A Tree Equity Score is a metric that helps cities assess how well they are delivering equitable tree canopy cover to all residents. The score combines measures of tree canopy cover need and priority for trees in urban neighborhoods. It is derived from tree canopy cover, climate, demographic and socioeconomic data.",
    format: formatFixed,
    sliderOptions: {
      tipFormatter: formatFixed,
    },
    field: "tes",
    range: [0, 100],
    expression: {
      "fill-opacity": 0.7,
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "tes"],
        30,
        "orange",
        100,
        GREEN,
      ],
    },
  },
  TREE_CANOPY_POSSIBLE_PERCENT: {
    label: "Possible tree canopy cover %",
    short: "Possible tree canopy cover %",
    format: formatPercent,
    field: "PossTCPct",
    expression: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "PossTCPct"],
        0.2,
        ORANGE,
        0.5,
        GREEN,
      ],
    },
  },
  // Necessary because of data inconsistency: this
  // level calls its tes 'tesctymean'
  MUNICIPALITY_TREE_EQUITY_SCORE: {
    label: "Tree Equity Score",
    short: "Tree Equity Score",
    explanation:
      "An indicator of whether an area has a sufficient amount of tree canopy cover distributed in a way that all residents can experience the climate, health and other benefits that trees provide.",
    range: [0, 100],
    format: formatFixed,
    field: "tesctymean",
    expression: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "tesctymean"],
        50,
        ORANGE,
        100,
        GREEN,
      ],
    },
  },
  POVERTY_PERCENT: {
    label: "People in poverty",
    short: "People in Poverty",
    explanation:
      "Percentage of people in a Block Group living on incomes below 200% of the federally-designated poverty line.",
    range: [0, 1],
    format: formatPercent,
    field: "pctpov",
    expression: {
      "fill-opacity": 0.7,
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", `pctpov`],
        0,
        "orange",
        0.9,
        "brown",
      ],
    },
  },
  TREE_CANOPY_PERCENT: {
    label: "Tree canopy cover %",
    short: "Tree canopy %",
    format: formatPercent,
    range: [0, 1],
    field: "TrCan_Pct",
    parcelField: "BG_TrCan_P",
    expression: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "TrCan_Pct"],
        0,
        ORANGE,
        0.5,
        GREEN,
      ],
    },
  },
  // Necessary because our scale for tree canopy percent is different
  // between other layers and parcels
  PARCEL_TREE_CANOPY_PERCENT: {
    label: "Tree canopy cover %",
    short: "Tree canopy %",
    format: formatPercent,
    range: [0, 1],
    field: "TrCan_Pct",
    expression: {
      "fill-opacity": 0.7,
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "TrCan_Pct"],
        0,
        "orange",
        0.9,
        GREEN,
      ],
    },
  },
  TREE_CANOPY_GOAL: {
    label: "Tree canopy cover goal %",
    short: "Tree Canopy Goal",
    explanation:
      "The percentage of tree canopy recommended for a particular neighborhood, city, or region based on natural biom and population density.",
    range: [0, 0.5],
    format: formatPercent,
    field: "tc_goal",
    expression: {
      "fill-opacity": 0.7,
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "tc_goal"],
        0.2,
        GREEN,
        0.48,
        "#72B252",
      ],
    },
  },
  TREE_CANOPY_GAP: {
    label: "Tree canopy cover gap %",
    short: "Tree canopy gap",
    explanation:
      "The difference between the percentage of tree canopy cover and tree canopy goal.",
    format: formatPercent,
    range: [0, 0.5],
    field: "tc_gap",
    expression: {
      "fill-opacity": 0.7,
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "tc_gap"],
        0,
        GREEN,
        0.46,
        "orange",
      ],
    },
  },
  HEALTH_EQUITY_ZONE: {
    label: "Health Equity Zones",
    short: "Health Equity Zones",
    explanation:
      " Geographic areas in Rhode Island where existing opportunities emerge and investments are made to address differences in health outcomes. Through a collaborative, community-led process, each Health Equity Zone conducts a needs assessment and implements a data-driven plan of action to address the unique social, economic and environmental factors preventing people from being as healthy as possible.",
    format: (val, kind) => {
      return kind === "legend" ? (
        val
      ) : (
        <div className="total-list__value-small">{val}</div>
      );
    },
    field: "HEZ",
    expression: {
      "fill-opacity": 0.7,
      "fill-color": ["case", ["!=", ["get", "HEZ"], " "], GREEN, ORANGE],
    },
  },
  PEOPLE_OF_COLOR: {
    label: "People of color %",
    short: "People of Color",
    explanation:
      "Percentage of people in a Block Group that are Black or African American, American Indian or Alaska Native, Asian, Native Hawaiian or Other Pacific Islander, and includes all people classified as Hispanic by the Census Bureau.",
    range: [0, 1],
    format: formatPercent,
    field: "pctpoc",
    expression: {
      "fill-opacity": 0.7,
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "pctpoc"],
        0,
        "orange",
        0.9,
        "brown",
      ],
    },
  },
  CHILDREN_PERCENT: {
    label: "Children (0-17) %",
    short: "Children (0-17)",
    explanation:
      "Percentage of children, ages 0 to 17 years of age, in a Block Group.",
    range: [0, 0.52],
    format: formatPercent,
    field: "child_perc",
    expression: {
      "fill-opacity": 0.7,
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "child_perc"],
        0,
        "orange",
        0.6,
        "brown",
      ],
    },
  },
  SENIOR_PERCENT: {
    label: "Seniors (65+) %",
    short: "Seniors (65+)",
    explanation: "Percentage of seniors, ages 65 and older, in a Block Group.",
    range: [0, 0.63],
    format: formatPercent,
    field: "seniorperc",
    expression: {
      "fill-opacity": 0.7,
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", `seniorperc`],
        0,
        "orange",
        0.65,
        "brown",
      ],
    },
  },
  TEMP: {
    label: "Surface Temperature",
    short: "Surface Temperature",
    format: formatFixed,
    field: "temp",
    explanation:
      "Land surface temperature calculated using LandSAT8 over multiple years in the summer months.",
    expression: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "temp"],
        70,
        "orange",
        88,
        "red",
      ],
    },
  },
  AFTER_AIR: {
    label: "Afternoon Air Temperature",
    short: "Afternoon Air Temperature",
    format: formatFixed,
    field: "aftairavg",
    explanation:
      "Air temperature derived from an an AI model developed by CAPA Strategies using training data collected by researchers riding their bikes in the afternoon for one day in the summer of 2020.",
    expression: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "aftairavg"],
        80,
        "orange",
        92,
        "red",
      ],
    },
  },
  EVE_AIR: {
    label: "Evening Air Temperature",
    short: "Evening Air Temperature",
    format: formatFixed,
    field: "eveairavg",
    explanation:
      "Air temperature derived from an an AI model developed by CAPA Strategies using training data collected by researchers riding their bikes in the evening for one day in the summer of 2020.",
    expression: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "eveairavg"],
        74,
        "orange",
        86,
        "red",
      ],
    },
  },
  HEALTH_NORM: {
    label: "Health Risk Indicator",
    short: "Health Risk Indicator",
    format: formatScore,
    field: "health_nor",
    expression: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "health_nor"],
        0,
        "orange",
        0.78,
        "red",
      ],
    },
  },
  CLIMATE_HEALTH_RISK: {
    label: "Climate-related health risk",
    short: "Climate-related health risk",
    explanation:
      "High, moderate and low numbers of hospitalizations and emergency room visits for conditions that are exacerbated by a changing climate. The conditions combined in this measure include diabetes, mental health, asthma and other respiratory issues, heart and kidney diseases.",
    format: formatIdentity,
    field: "HealthRisk",
    expression: {
      "fill-opacity": 0.7,
      "fill-color": [
        "match",
        ["get", "HealthRisk"],
        "Low",
        GREEN,
        "Moderate",
        ORANGE,
        "High",
        "brown",
        "rgba(255,255,255,0)",
      ],
    },
  },
  UNEMPLOYMENT_RATE: {
    label: "Unemployment",
    short: "Unemployment",
    explanation:
      "Percentage of the labor force that do not have a job, are available, and looking for one).",
    range: [0, 0.4],
    format: formatPercent,
    field: "unemplrate",
    expression: {
      "fill-opacity": 0.7,
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "unemplrate"],
        0,
        "orange",
        0.5,
        "brown",
      ],
    },
  },
  FLOOD_RISK: {
    label: "Flood risk",
    short: "Flood risk",
    explanation:
      "An estimate of high (100-year flood zone), moderate (500-year flood zone), and low flood risk based on FEMA flood designations.",
    format: formatIdentity,
    field: "Flood",
    expression: {
      "fill-color": [
        "match",
        ["get", "Flood"],
        "Low",
        GREEN,
        "Moderate",
        "#FDDEAD",
        "High",
        "#F9B84F",
        "rgba(255,255,255,0)",
      ],
    },
  },
  TREE_CANOPY_POSSIBLE: {
    label: "Possible tree canopy",
    short: "Possible tree canopy",
    format: (value) => (
      <>
        {withCommas(value)}
        <sub className="total-list__subscript"> sqm</sub>
      </>
    ),
    field: "PossibleTC",
    expression: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "PossibleTC"],
        0,
        ORANGE,
        25000,
        GREEN,
      ],
    },
  },
  PUBLIC: {
    label: "Ownership",
    short: "Ownership",
    format: (value) => (value === "Y" ? "Public" : "Private"),
    field: "Public",
    expression: {
      "fill-color": [
        "match",
        ["get", "Public"],
        "Y",
        GREEN,
        "N",
        ORANGE,
        "rgba(255,255,255,0)",
      ],
    },
  },
};

const LANDUSE_SOURCES = [
  "tesa-app.landuseLC_BARRINGTON",
  "tesa-app.landuseLC_BRISTOL",
  "tesa-app.landuseLC_BURRILLVILLE",
  "tesa-app.landuseLC_CENTRALFALLS",
  "tesa-app.landuseLC_CHARLESTOWN",
  "tesa-app.landuseLC_COVENTRY",
  "tesa-app.landuseLC_CRANSTON",
  "tesa-app.landuseLC_CUMBERLAND",
  "tesa-app.landuseLC_EASTGREENWICH",
  "tesa-app.landuseLC_EASTPROVIDENCE",
  "tesa-app.landuseLC_EXETER",
  "tesa-app.landuseLC_GLOCESTER",
  "tesa-app.landuseLC_HOPKINTON",
  "tesa-app.landuseLC_JAMESTOWN",
  "tesa-app.landuseLC_JOHNSTON",
  "tesa-app.landuseLC_LINCOLN",
  "tesa-app.landuseLC_MIDDLETOWN",
  "tesa-app.landuseLC_NARRAGANSETT",
  "tesa-app.landuseLC_NEWPORT",
  "tesa-app.landuseLC_NORTHKINGSTOWN",
  "tesa-app.landuseLC_NORTHPROVIDENCE",
  "tesa-app.landuseLC_NORTHSMITHFIELD",
  "tesa-app.landuseLC_PAWTUCKET",
  "tesa-app.landuseLC_PORTSMOUTH",
  "tesa-app.landuseLC_PROVIDENCE",
  "tesa-app.landuseLC_SCITUATE",
  "tesa-app.landuseLC_SMITHFIELD",
  "tesa-app.landuseLC_SOUTHKINGSTOWN",
  "tesa-app.landuseLC_TIVERTON",
  "tesa-app.landuseLC_WARREN",
  "tesa-app.landuseLC_WARWICK",
  "tesa-app.landuseLC_WESTERLY",
  "tesa-app.landuseLC_WESTGREENWICH",
  "tesa-app.landuseLC_WOONSOCKET",
  "tesa-app.landuseLC_WESTWARWICK",
].map((layerId) => {
  return {
    layerId,
    layerName: "original",
  };
});

export const MUNICIPALITIES = {
  layerId: "tesa-app.landuseMunicipalities",
  layerName: "original",
};

export const BLOCKGROUPS = {
  layerId: "tesa-app.cs8ifu7c",
  layerName: "blockgroups_tesa-0qg3qt",
};

export const PARCELS = [
  {
    layerId: "tesa-app.landuseAParcels",
    layerName: "original",
  },
  {
    layerId: "tesa-app.landuseBParcels",
    layerName: "original",
  },
  {
    layerId: "tesa-app.landuseCParcels",
    layerName: "original",
  },
];

export const INITIAL_INDICATORS = {
  MUNICIPALITY: INDICATORS.MUNICIPALITY_TREE_EQUITY_SCORE,
  BLOCKGROUP: INDICATORS.TREE_EQUITY_SCORE,
  PARCEL: INDICATORS.TREE_CANOPY_PERCENT,
  CANOPY: INDICATORS.TREE_CANOPY_PERCENT,
};

export const LEVEL_LAYERS: LevelLayersIndex = {
  BLOCKGROUP: ["layer-block-fills"],
  MUNICIPALITY: ["layer-municipality-fills"],
  PARCEL: [],
  CANOPY: [],
};

const PARCEL_FILLS: MapLayer[] = [];

for (let i = 0; i < PARCELS.length; i++) {
  let parcelLayer = PARCELS[i];
  const id = `layer-parcels-fill-${i}`;
  LEVEL_LAYERS.PARCEL.push(id);
  PARCEL_FILLS.push({
    id,
    source: `source-parcels-${i}`,
    "source-layer": `${parcelLayer.layerName}`,
    type: "fill",
    minzoom: PARCEL_ZOOM,
    paint: INITIAL_INDICATORS.PARCEL.expression,
    before: LAYER_BEFORE,
  });
}

export const LANDUSE_LAYERS: MapLayer[] = LANDUSE_SOURCES.map((source) => {
  return {
    id: `landuse-${source.layerId}`,
    source: source.layerId,
    "source-layer": "original",
    type: "fill",
    minzoom: CANOPY_ZOOM,
    paint: {
      "fill-opacity": 0.6,
      "fill-color": [
        "match",
        ["get", "gridcode"],
        1,
        GREEN,
        "rgba(0, 0, 0, 0)",
      ],
      "fill-outline-color": [
        "match",
        ["get", "gridcode"],
        // Canopy
        5,
        "rgba(0, 0, 0, 0)",
        6,
        "rgba(0, 0, 0, 0)",
        7,
        "rgba(0, 0, 0, 0)",
        "black",
      ],
    },
  };
});

const topLayers: MapLayer[] = [
  {
    id: "layer-block-lines",
    source: `source-blocks`,
    "source-layer": BLOCKGROUPS.layerName,
    minzoom: BLOCKGROUP_ZOOM,
    before: LAYER_BEFORE,
    type: "line",
    paint: {
      "line-color": [
        "case",
        ["in", ["get", "GEOID"], ["literal", []]],
        HIGHLIGHT_COLOR,
        DEFAULT_COLOR,
      ],
      "line-width": ["case", ["in", ["get", "GEOID"], ["literal", []]], 2.5, 1],
      "line-opacity": 1,
    },
  },
  {
    id: "layer-municipality-lines",
    source: `source-municipalities`,
    "source-layer": MUNICIPALITIES.layerName,
    before: LAYER_BEFORE,
    maxzoom: PARCEL_ZOOM,
    type: "line",
    paint: {
      "line-color": [
        "case",
        ["in", ["get", "NAME"], ["literal", []]],
        HIGHLIGHT_COLOR,
        DEFAULT_COLOR,
      ],
      "line-width": [
        "case",
        ["in", ["get", "NAME"], ["literal", []]],
        MUNICIPALITY_LINE_WIDTH_HIGHLIGHT,
        MUNICIPALITY_LINE_WIDTH_DEFAULT,
      ],
    },
  },
  {
    id: "scenario-features-fill",
    source: `source-features`,
    type: "fill",
    paint: {
      "fill-color": "#D20C0C",
      "fill-outline-color": "#D20C0C",
      "fill-opacity": 0.2,
    },
  },
  {
    id: "scenario-features",
    source: `source-features`,
    type: "line",
    paint: {
      "line-color": "black",
      "line-width": 2,
    },
  },
];

const middleLayers: MapLayer[] = [
  {
    id: BLOCKGROUP_DATA_LAYER,
    source: `source-blocks`,
    "source-layer": BLOCKGROUPS.layerName,
    before: LAYER_BEFORE,
    type: "fill",
    paint: {
      "fill-opacity": 0,
    },
  },
  {
    id: "layer-block-fills",
    source: `source-blocks`,
    "source-layer": BLOCKGROUPS.layerName,
    before: LAYER_BEFORE,
    minzoom: BLOCKGROUP_ZOOM,
    maxzoom: PARCEL_ZOOM,
    type: "fill",
    paint: INITIAL_INDICATORS.BLOCKGROUP.expression,
  },
];

const bottomLayers: MapLayer[] = [
  {
    id: "layer-municipality-data",
    source: `source-municipalities`,
    "source-layer": MUNICIPALITIES.layerName,
    type: "fill",
    paint: {
      "fill-opacity": 0,
    },
  },
  {
    id: "layer-municipality-fills",
    source: `source-municipalities`,
    "source-layer": MUNICIPALITIES.layerName,
    before: LAYER_BEFORE,
    type: "fill",
    maxzoom: BLOCKGROUP_ZOOM,
    paint: INITIAL_INDICATORS.MUNICIPALITY.expression,
  },
];

export const MAP_LAYERS: MapLayer[] = bottomLayers
  .concat(LANDUSE_LAYERS)
  .concat(middleLayers)
  .concat(PARCEL_FILLS)
  .concat(topLayers);

export const PARCEL_LINE_LAYERS: string[] = [];

for (let i = 0; i < PARCELS.length; i++) {
  let parcelLayer = PARCELS[i];
  const id = `layer-parcels-line-${i}`;
  PARCEL_LINE_LAYERS.push(id);

  MAP_LAYERS.push({
    id: id,
    source: `source-parcels-${i}`,
    "source-layer": `${parcelLayer.layerName}`,
    type: "line",
    minzoom: PARCEL_ZOOM,
    paint: {
      "line-color": DEFAULT_COLOR,
      "line-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        BLOCKGROUP_ZOOM,
        0.01,
        22,
        2,
      ],
    },
    before: LAYER_BEFORE,
  });
}

export const DRAW_STYLE = [
  // ACTIVE (being drawn)
  // line stroke
  {
    id: "gl-draw-line",
    type: "line",
    filter: ["all", ["==", "$type", "LineString"], ["!=", "mode", "static"]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#D20C0C",
      "line-dasharray": [0.2, 2],
      "line-width": 2,
    },
  },
  // polygon fill
  {
    id: "gl-draw-polygon-fill",
    type: "fill",
    filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
    paint: {
      "fill-color": "#D20C0C",
      "fill-outline-color": "#D20C0C",
      "fill-opacity": 0.2,
    },
  },
  // polygon outline stroke
  // This doesn't style the first edge of the polygon, which uses the line stroke styling instead
  {
    id: "gl-draw-polygon-stroke-active",
    type: "line",
    filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#D20C0C",
      "line-dasharray": [0.2, 2],
      "line-width": 3,
    },
  },
  // vertex point halos
  {
    id: "gl-draw-polygon-and-line-vertex-halo-active",
    type: "circle",
    filter: [
      "all",
      ["==", "meta", "vertex"],
      ["==", "$type", "Point"],
      ["!=", "mode", "static"],
    ],
    paint: {
      "circle-radius": 5,
      "circle-color": "#FFF",
    },
  },
  // vertex points
  {
    id: "gl-draw-polygon-and-line-vertex-active",
    type: "circle",
    filter: [
      "all",
      ["==", "meta", "vertex"],
      ["==", "$type", "Point"],
      ["!=", "mode", "static"],
    ],
    paint: {
      "circle-radius": 3,
      "circle-color": "#D20C0C",
    },
  },

  // INACTIVE (static, already drawn)
  // line stroke
  {
    id: "gl-draw-line-static",
    type: "line",
    filter: ["all", ["==", "$type", "LineString"], ["==", "mode", "static"]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#000",
      "line-width": 3,
    },
  },
  // polygon fill
  {
    id: "gl-draw-polygon-fill-static",
    type: "fill",
    filter: ["all", ["==", "$type", "Polygon"], ["==", "mode", "static"]],
    paint: {
      "fill-color": "#000",
      "fill-outline-color": "#000",
      "fill-opacity": 0.1,
    },
  },
  // polygon outline
  {
    id: "gl-draw-polygon-stroke-static",
    type: "line",
    filter: ["all", ["==", "$type", "Polygon"], ["==", "mode", "static"]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#000",
      "line-width": 3,
    },
  },
];

export function addVectorSources(map: mapboxgl.Map) {
  for (let [name, url] of [
    ["source-blocks", `mapbox://${BLOCKGROUPS.layerId}`],
    ["source-municipalities", `mapbox://${MUNICIPALITIES.layerId}`],
  ]) {
    map.addSource(name, {
      type: "vector",
      url,
    });
  }
  for (let { layerId } of LANDUSE_SOURCES) {
    map.addSource(layerId, {
      type: "vector",
      url: `mapbox://${layerId}`,
    });
  }
  for (let i = 0; i < PARCELS.length; i++) {
    let { layerId } = PARCELS[i];
    map.addSource(`source-parcels-${i}`, {
      type: "vector",
      url: `mapbox://${layerId}`,
    });
  }
  map.addSource("source-features", {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: [],
    },
  });
}

export function addLayers(map: mapboxgl.Map) {
  for (let layerSpec of MAP_LAYERS) {
    const { before, ...layer } = layerSpec;
    map.addLayer(layer, before);
  }
}

export function addFeatures(map: any, scenario: Scenario) {
  let features: any = [];
  if (scenario) {
    for (let municipality of scenario.municipalities) {
      for (let blockgroup of municipality.blockgroups) {
        features = features.concat(blockgroup.features || []);
      }
    }
  }
  map.getSource("source-features").setData({
    type: "FeatureCollection",
    features: features,
  });
  return features;
}

export function highlightScenario(
  map: mapboxgl.Map,
  scenario: Scenario,
  hoverFeature?: Blockgroup | Parcel
) {
  let blockgroupIds = [];
  let municipalityNames = [];
  let parcelIds = [];
  let hoverParcel = [];
  let hoverBlockgroup = [];

  if (hoverFeature) {
    if ("PARCEL_ID" in hoverFeature) {
      hoverParcel.push(hoverFeature.PARCEL_ID);
    } else {
      hoverBlockgroup.push(hoverFeature.GEOID);
    }
  }

  if (scenario) {
    for (let municipality of scenario.municipalities) {
      municipalityNames.push(municipality.NAME);
      for (let blockgroup of municipality.blockgroups) {
        blockgroupIds.push(blockgroup.GEOID);
        for (let parcel of blockgroup.parcels) {
          parcelIds.push(parcel.PARCEL_ID);
        }
      }
    }
  }

  {
    const hc = ["in", ["get", "GEOID"], ["literal", hoverBlockgroup]];
    const condition = ["in", ["get", "GEOID"], ["literal", blockgroupIds]];
    map.setPaintProperty("layer-block-lines", "line-width", [
      "case",
      condition,
      2.5,
      1,
    ]);
    map.setPaintProperty("layer-block-lines", "line-color", [
      "case",
      hc,
      "#ff8000",
      condition,
      HIGHLIGHT_COLOR,
      DEFAULT_COLOR,
    ]);
  }
  {
    const condition = ["in", ["get", "NAME"], ["literal", municipalityNames]];
    map.setPaintProperty("layer-municipality-lines", "line-width", [
      "case",
      condition,
      MUNICIPALITY_LINE_WIDTH_HIGHLIGHT,
      MUNICIPALITY_LINE_WIDTH_DEFAULT,
    ]);
    map.setPaintProperty("layer-municipality-lines", "line-color", [
      "case",
      condition,
      HIGHLIGHT_COLOR,
      DEFAULT_COLOR,
    ]);
  }
  {
    const hc = ["in", ["get", "PARCEL_ID"], ["literal", hoverParcel]];
    const condition = ["in", ["get", "PARCEL_ID"], ["literal", parcelIds]];
    for (let parcelLayer of PARCEL_LINE_LAYERS) {
      const widthExpression = [
        "interpolate",
        ["linear"],
        ["zoom"],
        PARCEL_ZOOM,
        ["case", condition, 2, 0.01],
        22,
        ["case", condition, 5, 2],
      ];
      map.setPaintProperty(parcelLayer, "line-width", widthExpression);
      map.setPaintProperty(parcelLayer, "line-color", [
        "case",
        hc,
        "#ff8000",
        condition,
        HIGHLIGHT_COLOR,
        DEFAULT_COLOR,
      ]);
    }
  }
}
